<template>
  <div v-if="initialized" class="global d-flex flex-column">
    <div class="scroll-ctn">
      <v-row v-if="enabledLanguages.length === 1" class="my-7" align="center" justify="center">
        <v-col class="translations-languages-selection">
          <div class="label font-weight-bold">
            {{$t('translations')}}
          </div>

          <div class="font-weight-light py-2">
            {{$t('translations_select_languages_description')}}
          </div>

          <v-autocomplete
            v-model="selectedTranslationLanguages"
            :items="allLanguagesDropdown"
            :search-input.sync="allLanguagesSearchInput"
            @change="allLanguagesSearchInput=''"
            item-text="name"
            item-value="key"
            chips
            :label="$t('select_languages')"
            :placeholder="$t('search_languages')"
            multiple
            class="my-3"
          >
            <template v-slot:selection="data">
              <v-chip
                v-bind="data.attrs"
                :input-value="data.selected"
                close
                @click="data.select"
                @click:close="removeSelectedTranslationLanguage(data.item)"
              >
                {{ data.item.name }}
              </v-chip>
            </template>
          </v-autocomplete>

          <div class="d-flex justify-end">
            <v-btn :disabled="!selectedTranslationLanguages" color="primary" raised @click="addEnabledLanguages()">{{$t('continue')}}</v-btn>
          </div>
        </v-col>
      </v-row>

      <v-row v-if="enabledLanguages.length > 1" align="stretch" id="translations" class="ma-0">
        <v-col class="translation-sidebar flex-grow-0">
          <v-row align="center">
            <div class="title pl-2 flex-grow-1">{{$t('translations_language_list_title')}}</div>
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn v-bind="attrs" v-on="on" class="ma-2" icon color="primary" @click="exportAllTranslations()">
                  <v-icon>
                     mdi-download
                  </v-icon>
                </v-btn>
              </template>
              <span>{{$t('translations_export_all')}}</span>
            </v-tooltip>
          </v-row>
          <div class="published-lang-ctn ma-3 py-1 text-center">{{$t('translations_language_published', { publishedLanguages: published.reviewed_languages ? published.reviewed_languages.length : 0, totalLanguages: enabledLanguages.length })}}</div>
          <v-row align="center" justify="center">
            <v-btn @click="machineTranslateDialog = true" :loading="translating" class="my-3 btn-text-white" color="primary">
              {{$t('translations_empty_state_machine_translate')}}
            </v-btn>
            <!-- <feature-lock class="ml-2" v-if="!isAvailable('elabel:translations:machine_translation')"></feature-lock> -->
          </v-row>

          <v-row align="center" justify="space-between" class="font-weight-bold ma-0">
            <v-col class="ma-0 pa-0 pl-2 my-1">
              <div class="d-flex align-center">
                <div class="mr-1">{{$t('languages')}}</div>
                <v-tooltip right>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn v-bind="attrs" v-on="on" icon color="primary" @click="toggleLanguagesEditMode()">
                      <v-icon color="primary">$edit</v-icon>
                    </v-btn>
                  </template>
                  <span>{{$t('translations_edit_languages')}}</span>
                </v-tooltip>
              </div>
            </v-col>
            <v-col class="ma-0 pa-0 pl-2 my-1" align="center">
              <div>{{$t('translations_status')}}</div>
            </v-col>
          </v-row>
          <v-row  :class="{ 'selected' : selectedLanguage === lang.key}" @click="selectLanguage(lang.key)" class="language ma-0 px-3 py-0" align="center" justify="space-between" v-for="lang in enabledLanguagesSorted" :key="`languages-${lang.key}`">
            <v-col cols="8" class="ma-0 pa-0">
              {{ $t(`languages_${lang.key}`) }}{{ lang.key === defaultLanguage ? ` (${$t('reference')})` : '' }}
            </v-col>

            <v-col cols="4" class="ma-0 pa-0">
              <div class="d-flex align-center">
                <div class="lang-pill" :class="getLangStatus(lang.key)" />
                <span>{{ $t(`translations_lang_status_${getLangStatus(lang.key)}`) }}</span>
              </div>
            </v-col>
          </v-row>

          <v-btn class="publish-btn btn-text-white my-2" color="primary" :loading="publishing" block raised @click="publish()">{{$t('publish_approved_languages')}}</v-btn>
          <v-btn v-if="published.reviewed_languages && published.reviewed_languages.length < enabledLanguages.length" class="publish-btn my-2" color="primary" outlined text block @click="bulkApproveDialog = true">{{$t('bulk_approve_and_publish_languages')}}</v-btn>
        </v-col>

        <v-col class="translation-content flex-grow-1" v-if="selectedLanguage">
          <v-row class="ma-2" align="center" justify="space-between">
            <div class="title my-2">{{$t('translations_review_title')}}</div>
            <div>
              <v-tooltip left>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn v-bind="attrs" v-on="on" @click="expandPreview" color="primary" class="hide-lg mr-2" icon>
                    <v-icon>mdi-cellphone-link</v-icon>
                  </v-btn>
                </template>
                <span>{{$t('preview_title')}}</span>
              </v-tooltip>

              <v-btn raised v-if="isPublishing" :disabled="isPublishing">{{$t('publishing')}}</v-btn>
              <v-btn raised class="btn-text-white" color="primary" v-else-if="getLangStatus(selectedLanguage) === 'reviewing'" @click="approveLanguage(selectedLanguage)">{{$t('translations_approve')}}</v-btn>
              <v-btn raised class="btn-text-white" color="primary" v-else-if="getLangStatus(selectedLanguage) === 'approved'" @click="publish([selectedLanguage])">{{$t('publish')}}</v-btn>
              <!-- <v-btn raised class="translations" color="primary" v-if="getLangStatus(selectedLanguage) === 'approved'" :disabled="selectedLanguage === defaultLanguage" @click="unapproveLanguage(selectedLanguage)">{{$t('translations_unapprove')}}</v-btn> -->
              <v-btn raised class="btn-text-white" color="primary" v-else-if="getLangStatus(selectedLanguage) === 'published' && selectedLanguage !== defaultLanguage" @click="unpublishLanguage(selectedLanguage)">{{$t('translations_unpublish')}}</v-btn>
            </div>
          </v-row>
          <v-row v-if="selectedLanguage !== defaultLanguage" class="hint body-2 ma-0 py-2" align="center">
            {{$t('translations_machine_translate_hint')}}
          </v-row>

          <div class="translation-table">
            <div class="translation-table-header d-flex align-center font-weight-bold">
              <div class="min-w-60 pl-3">{{$t('translations_review_reference')}}: {{$t(`languages_${defaultLanguage}`)}}</div>
              <v-row align="center" justify="space-between" class="ma-0 pl-2 py-2">
                <div>{{$t(`languages_${selectedLanguage}`)}}</div>
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn v-bind="attrs" v-on="on" class="mr-4 btn-text-white" color="white" @click="exportTranslations()" icon>
                      <v-icon>
                        mdi-download
                      </v-icon>
                    </v-btn>
                  </template>
                  <span>{{$t('translations_export')}}</span>
                </v-tooltip>
              </v-row>
            </div>
            <v-row align="center" class="translation-table-subheader font-weight-bold ma-0">
              <v-col class="min-w-60 ma-0 pa-0">
                <v-row class="ma-0" align="center" justify="space-between">
                  <v-col class="ma-0 pa-0">{{$t('translations_table_field')}}</v-col>
                  <v-col class="ma-0 pa-0 pl-2">{{$t('translations_table_value')}}</v-col>
                </v-row>
              </v-col>
              <v-col class="ma-0 pa-0 pl-2 py-1">
                <v-row align="center" class="ma-0 px-1">
                  <div class="mr-1">{{$t('translations')}}</div>
                  <v-tooltip v-if="selectedLanguage !== defaultLanguage" bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon color="primary" v-bind="attrs" v-on="on">mdi-lightbulb-outline</v-icon>
                    </template>
                    <div class="csv-hint">
                      <div class="hint-title mb-2">{{$t('translations_csv_hint_1')}}:</div>
                      <ul class="my-2">
                        <li class="font-weight-light">{{$t('translations_csv_hint_2')}}</li>
                        <li class="font-weight-light">{{$t('translations_csv_hint_3')}}</li>
                      </ul>
                      <img src="@/assets/translation-csv-hint.png" alt="">
                      <div class="warning font-weight-bold">{{$t('translations_csv_hint_4')}}</div>
                    </div>
                  </v-tooltip>
                  <v-spacer></v-spacer>
                  <v-tooltip  v-if="selectedLanguage !== defaultLanguage" top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn v-bind="attrs" v-on="on" :loading="translating" :disabled="translating" @click="machineTranslate()" color="primary" icon><v-icon>mdi-translate</v-icon></v-btn>
                    </template>
                    <span>{{$t('translations_machine_translate_warning_title')}}</span>
                  </v-tooltip>
                </v-row>
              </v-col>
            </v-row>
            <div class="translation-groups">
              <div class="translation-group" v-for="(groupItems, groupName, groupIndex) in groupedTranslations" :key="groupName">
                <v-row align="center" class="ma-0" @click="toggleGroup(groupName)">
                  <v-col class="group-header d-flex align-center ma-0 py-3 font-weight-bold">
                    <v-icon class="mr-2" color="black">{{ groupIcon(groupName) }}</v-icon>
                    <span>{{ groupTitle(groupName, groupIndex) }}</span>
                  </v-col>
                </v-row>

                <div v-show="!collapsedGroups[groupName]">
                  <v-row class="group-row align-stretch" v-for="(item) in groupItems" :key="`translations-${selectedLanguage}-${item.key}`">
                    <v-col class="min-w-60 ma-0 pa-0" :class="item.type === 'divider' ? 'medium-gray-bg' : 'light-gray-bg'">
                      <v-row class="ma-0" align="start" justify="space-between">
                        <v-col class="translation-key font-weight-bold">
                          {{ getI18nKey(item.key, item.subGroupNumber) }}
                        </v-col>
                        <v-col v-if="(item.type === 'image' || item.type === 'gallery') && isDisplayableImage(translatedStrings[item.key])" class="translation-value">
                          <img class="image-header" :src="translatedStrings[item.key]"/>
                        </v-col>
                        <div
                        v-else-if="componentMapping[item.type] && item.element !== 'button'"
                        class="rich-text-container w-49"
                        >
                          <component
                            :is="componentMapping[item.type]"
                            :content.sync="translationsStrings[item.key]"
                            :ref="'defaultLang-' + item.key"
                            :is-translation-edit="true"
                            :disable-rich-text="true"
                            :child-classes="customClasses(item.type, true)"
                          ></component>
                        </div>
                        <v-col v-else class="translation-value">{{ item.value }}</v-col>
                      </v-row>
                    </v-col>
                    <v-col v-if="selectedLanguage !== defaultLanguage" class="align-stretch ma-0 pa-0 max-w-40" :class="{ 'outline-blue' : item.type === 'image'}">
                      <div v-if="item.type === 'image' || item.type === 'gallery'" class="flex-shrink-0 mt-1">
                        <div v-if="!isEditingImageLink(item.key)" class="d-flex flex-column">
                          <div class="d-flex ml-1">
                            <image-cropper v-if="item.type === 'image'" :input-key="`image-cropper-${groupName}-${item.key}`" @image-upload="(url) => onImageUpload(item.key, url)" :tab-id="groupName" :icon="true"></image-cropper>

                            <v-tooltip top v-if="item.type === 'image'">
                              <template v-slot:activator="{ on, attrs }">
                                <v-btn v-bind="attrs" v-on="on" icon color="primary" @click="handleSTGalleryBtnClick(item.key)" v-if="isAllowed('gallery_view')">
                                  <v-icon>mdi-image-outline</v-icon>
                                </v-btn>
                              </template>
                              <span>{{$t('translations_select_image')}}</span>
                            </v-tooltip>

                            <v-tooltip top v-if="item.type === 'image' && isAvailable('editor:tabs:tools:image:link')">
                              <template v-slot:activator="{ on, attrs }">
                                <v-btn v-bind="attrs" v-on="on"  @click="toggleImageEditMode(item.key, item.type)" color="accent" icon>
                                  <v-icon>mdi-link</v-icon>
                                </v-btn>
                              </template>
                              <span>{{$t('translations_add_picture')}}</span>
                            </v-tooltip>
                          </div>
                          <v-col class="translation-value cursor-default">{{ translatedStrings[item.key] }}</v-col>
                        </div>
                        <div v-if="isEditingImageLink(item.key)" class="d-flex flex-column">
                          <div class="edit-image-toolbar">
                            <div class="edit-image-toolbar-actions">
                              <v-btn @click="saveImageLink(item.key)" color="accent" icon>
                                <v-icon>$check</v-icon>
                              </v-btn>
                              <v-btn @click="cancelEditingImageLink(item.key)" color="accent" icon class="mr-1">
                                <v-icon >$cancel</v-icon>
                              </v-btn>
                            </div>
                          </div>
                          <image-edit v-if="item.type === 'image'" :content.sync="tempImageLinks[item.key]" :src.sync="translatedStrings[item.key]" :is-translation-edit="true"></image-edit>
                        </div>
                        <select-image-dialog
                        :display-select-image-dialog="activeGalleryKey === item.key"
                        @update:displaySelectImageDialog="closeSTGalleryDialog()"
                        @gallery-selected="(url) => handleSTGallerySelected(item.key, url)"
                        :app-tags="['stc-editor-image']"
                        :isAllowedEdit="isAllowed('gallery_edit')"
                        ></select-image-dialog>
                      </div>
                      <div v-else-if="componentMapping[item.type] && item.element !== 'button'" class="rich-text-container outline-blue" @click="currentFieldIndex = item.cumulativeIndex">
                        <component
                          :is="componentMapping[item.type]"
                          :content.sync="translatedStrings[item.key]"
                          :ref="'editor-' + item.key"
                          :is-translation-edit="true"
                          :child-classes="customClasses(item.type)"
                          @rich-text-input-focused="handleRichTextFocus(item.key)"
                          @rich-text-input-blurred="currentFieldIndex = null"
                        ></component>
                      </div>
                      <div v-else-if="isFileUrlItem(item)" class="flex-shrink-0 mt-1">
                        <div class="d-flex flex-column">
                          <div class="d-flex ml-1">
                            <v-tooltip top v-if="isAllowed('gallery_view')">
                              <template v-slot:activator="{ on, attrs }">
                                <v-btn v-bind="attrs" v-on="on" icon color="primary" @click="handleSTGalleryBtnClick(item.key)">
                                  <v-icon>mdi-file-pdf-box</v-icon>
                                </v-btn>
                              </template>
                              <span>{{$t('translations_select_document')}}</span>
                            </v-tooltip>
                          </div>
                          <v-textarea
                            auto-grow
                            rows="1"
                            :data-input-index="item.cumulativeIndex"
                            small
                            dense
                            hide-details
                            v-model="translatedStrings[item.key]"
                            @input="handleTextAreaInput(item.key, item.type)"
                            class="ma-0 custom-textarea"
                          ></v-textarea>
                        </div>
                        <select-image-dialog
                          file-type="pdf"
                          :display-select-image-dialog="activeGalleryKey === item.key"
                          @update:displaySelectImageDialog="closeSTGalleryDialog()"
                          @gallery-selected="(url) => handleSTGallerySelected(item.key, url)"
                          :app-tags="['stc-editor-image']"
                          :isAllowedEdit="isAllowed('gallery_edit')"
                        ></select-image-dialog>
                      </div>
                      <v-textarea
                        v-else
                        auto-grow
                        rows="1"
                        :data-input-index="item.cumulativeIndex"
                        small
                        dense
                        hide-details
                        v-model="translatedStrings[item.key]"
                        @input="handleTextAreaInput(item.key, item.type)"
                        @keydown.enter="preventLineBreak(item.key, $event)"
                        class="ma-0 custom-textarea"
                      ></v-textarea>
                    </v-col>
                    <v-col v-else class="align-stretch ma-0 pa-0 max-w-40">
                      <div
                        v-if="componentMapping[item.type] && item.element !== 'button'"
                        class="rich-text-container"
                      >
                        <component
                          :is="componentMapping[item.type]"
                          :content.sync="translatedStrings[item.key]"
                          :ref="'editor-' + item.key"
                          :is-translation-edit="true"
                          :disable-rich-text="true"
                          :child-classes="customClasses(item.type)"
                        ></component>
                      </div>
                      <v-col v-else class="translation-value">{{ item.value }}</v-col>
                    </v-col>
                  </v-row>
                </div>
              </div>
            </div>
          </div>
        </v-col>
      </v-row>

      <!-- Reference language modal -->
      <language-edit-modal
        v-model="languagesEditDialog"
        :allow-translations-edit="true"
        @default-language-changed="selectLanguage"
        @translation-languages-changed="onTranslationLanguagesChanged"
      />

      <v-dialog scrollable v-model="bulkApproveDialog" width="600">
        <v-card>
          <v-card-title>
            {{$t('translations_bulk_approve_and_publish_title')}}
          </v-card-title>
          <v-card-text>
            <v-autocomplete
              class="mt-2"
              solo
              v-model="languagesToPublish"
              :search-input.sync="enabledLanguagesSearchInput"
              multiple
              :items="enabledLanguagesDropdown"
              item-value="key"
              item-text="name"
            >
              <template v-slot:item="{ item, on, attrs }">
                <v-list-item v-on="on" v-bind="attrs">
                  <v-checkbox v-model="attrs.inputValue"></v-checkbox>
                  <v-list-item-content>
                    <v-list-item-title>
                      {{ item.name }}
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </template>
            </v-autocomplete>

            <div>{{$t('translation_bulk_approve_disclaimer')}}</div>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="primary" @click="bulkApproveDialog = false" text>{{$t('close')}}</v-btn>
            <v-btn @click="bulkApproveAndPublish(true)" raised color="primary" class="btn-text-white">
              {{$t('translations_approve_and_publish')}}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog scrollable v-model="machineTranslateDialog" width="500">
        <v-card>
          <v-card-title class="headline">{{$t('translations_machine_translate_warning_title')}}</v-card-title>
          <v-card-text>
            <div class="my-3 body-1">
              {{$t('translations_machine_translate_warning_description')}}
            </div>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="primary" @click="machineTranslateDialog = false" text>{{$t('close')}}</v-btn>
            <v-btn raised color="primary" :loading="translating" @click="translateAllLanguages(true)" text>{{$t('translations_empty_state_machine_translate')}}</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex'
import utils from '@/utils/debounce'
import translations from '@/utils/translations'
import DOMPurify from 'dompurify'
import Languages from '@/utils/languages'
import TextEdit from '@/components/editor/TextEdit'
import DividerEdit from '@/components/editor/DividerEdit'
import VideoEdit from '@/components/editor/VideoEdit'
import GalleryEdit from '@/components/editor/GalleryEdit'
import ImageEdit from '@/components/editor/ImageEdit'
import ImageCropper from '@/components/utils/ImageCropper'
import LanguageEditModal from '@/components/utils/LanguageEditModal'
import { SelectImageDialog } from '@scantrust/st-gallery'
import { removeHtmlTags, convertToSTCString } from '@/utils/configHelpers'
import amplitude from 'amplitude-js'

// Order matching the Footer & Consent tab; also marks these fields as single-line fields
const FOOTER_CONSENT_FIELD_ORDER = [
  'footer:body',
  'footer:background:image',
  'footer:privacy:url',
  'footer:gdpr:privacy',
  'footer:gdpr:cookie',
  'cookie:message',
  'cookie:accept',
  'cookie:decline',
  'cookie:policy',
  'cookie:link:url'
]

export default {
  name: 'GlobalTab',
  components: {
    'text-edit': TextEdit,
    'divider-edit': DividerEdit,
    'image-edit': ImageEdit,
    'image-cropper': ImageCropper,
    'gallery-edit': GalleryEdit,
    'video-edit': VideoEdit,
    LanguageEditModal,
    SelectImageDialog
  },
  computed: {
    ...mapGetters(['initialized', 'stcTabs', 'getTabVisibility', 'isMenuCollapsed', 'isAvailable', 'hasLimit', 'getLimit', 'getToolCount', 'editor', 'published', 'hasPublishedVersion', 'publishing', 'currentPlan', 'defaultLanguage', 'enabledLanguages', 'isAllowed']),

    allLanguagesDropdown () {
      const collator = new Intl.Collator(this.$i18n.locale)
      const sorted = Languages.list.slice().sort((a, b) =>
        collator.compare(this.$t(`languages_${a.key}`), this.$t(`languages_${b.key}`))
      )
      return sorted.filter((lang) => lang.key !== this.defaultLanguage)
    },

    enabledLanguagesSorted () {
      const collator = new Intl.Collator(this.$i18n.locale)
      return this.languages.list
        .filter(lang => this.editor.enabled_languages.includes(lang.key))
        .sort((a, b) => {
          if (a.key === this.defaultLanguage) return -1
          if (b.key === this.defaultLanguage) return 1
          return collator.compare(this.$t(`languages_${a.key}`), this.$t(`languages_${b.key}`))
        })
    },

    enabledLanguagesDropdown () {
      const languages = JSON.parse(JSON.stringify(this.enabledLanguagesSorted))
      languages.unshift({
        key: 'all', name: this.$t('languages_all')
      })
      return languages
    },

    translationsStringsWithoutHTMLTags () {
      return removeHtmlTags(this.translationsStrings)
    },

    groupedTranslations () {
      const tabGroups = {}
      // CumulativeIndex keeps track of the index across all groups, so that the paste handler works properly
      let cumulativeIndex = 0

      Object.entries(this.translationsStringsWithoutHTMLTags).forEach(([key, value]) => {
        // Example of key: 'cms_01:body:2:data:content:large_text'
        const splitKeys = key.split(':')
        const tabGroup = splitKeys[0]
        const subGroup = splitKeys.slice(0, 3).join(':')
        const fieldElement = splitKeys[splitKeys.length - 2]
        const fieldType = splitKeys[splitKeys.length - 1]

        if (!tabGroups[tabGroup]) {
          tabGroups[tabGroup] = []
        }

        const fieldsWithMatchingTabGroupAndType = Object.keys(this.translationsStringsWithoutHTMLTags).filter(k => {
          const keyParts = k.split(':')
          const type = keyParts[keyParts.length - 1]
          const group = keyParts[0]
          return group === tabGroup && type === fieldType
        })

        const uniqueSubGroups = new Set(fieldsWithMatchingTabGroupAndType.map(k => k.split(':').slice(0, 3).join(':')))
        const uniqueSubGroupsArray = Array.from(uniqueSubGroups)

        let subGroupNumber = ''
        if (uniqueSubGroupsArray.length > 1) {
          const subGroupToNumber = {}
          uniqueSubGroupsArray.forEach((subgroup, index) => {
            subGroupToNumber[subgroup] = index + 1
          })
          subGroupNumber = subGroupToNumber[subGroup] || 1
        }

        // rich text rows hide their HTML tags here (the tags are just formatting), but
        // in plain fields a typed "<b>" is part of the text itself, so show it as-is
        const displayValue = this.componentMapping[fieldType] ? value : this.translationsStrings[key]

        tabGroups[tabGroup].push({ key, value: displayValue, element: fieldElement, type: fieldType, subGroupNumber, cumulativeIndex })

        cumulativeIndex++
      })

      const orderedTabGroups = {}
      this.editor.tabs.forEach(tab => {
        if (tabGroups[tab]) {
          orderedTabGroups[tab] = tabGroups[tab]
        }
      })

      // adds back the non-tab groups (Authentication results, Track & Trace, ...),
      // keeping footer and cookie at the very bottom like on the landing page
      Object.keys(tabGroups)
        .filter(group => !this.editor.tabs.includes(group))
        .sort((a, b) => ['footer', 'cookie'].indexOf(a) - ['footer', 'cookie'].indexOf(b))
        .forEach(group => {
          orderedTabGroups[group] = tabGroups[group]
        })

      return orderedTabGroups
    },

    componentMapping () {
      return {
        large_text: 'text-edit',
        text: 'text-edit',
        consume: 'text-edit',
        'track-and-trace': 'text-edit',
        'custom-html': 'text-edit',
        body: 'text-edit'
        // Can add non text-edit components here as needed, but must pass 'isTranslationEdit' prop to the added component to disable its style bar, as those are not lang-specific
      }
    }
  },
  data () {
    return {
      languageInitialized: false,
      eventHandlerInitialized: false,
      languages: Languages,
      languagesEditDialog: false,
      bulkApproveDialog: false,
      machineTranslateDialog: false,
      translating: false,
      isPublishing: false,
      allLanguagesSearchInput: '',
      enabledLanguagesSearchInput: '',
      selectedLanguage: '',
      languagesToPublish: ['all'],
      translationsStrings: {},
      translatedStrings: {},
      translationsMap: {},
      collapsedGroups: {},
      currentFieldIndex: null,
      activeGalleryKey: null,
      tempImageLinks: {},
      debounceStringUpdate: null,
      debounceUpdateVideoUrl: null,
      selectedTranslationLanguages: []
    }
  },
  methods: {
    ...mapMutations({
      setPublishedData: 'SET_PUBLISHED_DATA',
      setEnabledLanguages: 'SET_ENABLED_LANGUAGES',
      approveLanguage: 'APPROVE_LANGUAGE',
      unapproveLanguage: 'UNAPPROVE_LANGUAGE',
      unpublishLanguage: 'UNPUBLISH_LANGUAGE',
      setPreviewLanguage: 'SET_PREVIEW_LANGUAGE',
      changePreviewMode: 'CHANGE_PREVIEW_MODE',
      toggleAppSidebar: 'TOGGLE_MENU'
    }),

    selectLanguage (lang) {
      this.selectedLanguage = lang
      this.currentFieldIndex = null
      for (const [key] of Object.entries(this.translationsStrings)) {
        const textEditComponent = this.$refs['editor-' + key]
        const componentInstance = Array.isArray(textEditComponent) ? textEditComponent[0] : textEditComponent
        if (componentInstance?.editorContentJson?.content) {
          this.$set(componentInstance.editorContentJson, 'content', '')
        }
      }
      this.translatedStrings = {}
      this.translationsStrings = {}

      const { translationsStrings, translatedStrings } = translations.getTranslationsForLanguage(this.editor, this.defaultLanguage, this.selectedLanguage, true)
      const sortedTranslationsStrings = this.sortTranslations(translationsStrings)
      const sortedTranslatedStrings = this.sortTranslations(translatedStrings)

      for (const [key, value] of Object.entries(sortedTranslationsStrings)) {
        const textEditComponent = this.$refs['defaultLang-' + key]
        const componentInstance = Array.isArray(textEditComponent) ? textEditComponent[0] : textEditComponent
        if (componentInstance) {
          this.$set(componentInstance.editorContentJson, 'content', value)
          const richTextInputComponent = componentInstance.$children[0]
          richTextInputComponent.updateContent(value)
        }
        this.$set(this.translationsStrings, key, value)
      }
      for (const [key, value] of Object.entries(sortedTranslatedStrings)) {
        const textEditComponent = this.$refs['editor-' + key]
        const componentInstance = Array.isArray(textEditComponent) ? textEditComponent[0] : textEditComponent
        if (componentInstance) {
          this.$set(componentInstance.editorContentJson, 'content', value)
          const richTextInputComponent = componentInstance.$children[0]
          richTextInputComponent.updateContent(value)
        }
        this.$set(this.translatedStrings, key, value)
      }
      this.setPreviewLanguage(this.selectedLanguage)
    },

    sortTranslations (translations) {
      // Group fields into tab groups
      const keys = Object.keys(translations)
      const tabGroups = {}
      keys.forEach(key => {
        const group = key.split(':')[0]
        if (!tabGroups[group]) {
          tabGroups[group] = []
        }
        tabGroups[group].push(key)
      })

      const reorderedTranslations = {}
      const tabOrder = this.editor.tabs
      tabOrder.forEach(tab => {
        if (tabGroups[tab]) {
          // Order fields within each group so that label is on top
          tabGroups[tab].sort((a, b) => {
            if (a.includes(':label') && !b.includes(':label')) return -1
            if (!a.includes(':label') && b.includes(':label')) return 1
            return 0
          })
          // Order tab groups by tab order
          tabGroups[tab].forEach(key => {
            if (key.startsWith('track-and-trace:label')) {
              const splitKeys = key.split(':')
              splitKeys[splitKeys.length - 1] = 'track-and-trace-tab-name'
              const newKey = splitKeys.join(':')
              reorderedTranslations[newKey] = translations[key]
            } else {
              reorderedTranslations[key] = translations[key]
            }
          })
        }
      })

      // adds back Authentication results
      Object.keys(tabGroups).forEach(group => {
        if (!tabOrder.includes(group)) {
          if (group === 'footer' || group === 'cookie') {
            tabGroups[group].sort((a, b) => FOOTER_CONSENT_FIELD_ORDER.indexOf(a) - FOOTER_CONSENT_FIELD_ORDER.indexOf(b))
          }
          tabGroups[group].forEach((key) => {
            reorderedTranslations[key] = translations[key]
          })
        }
      })

      return reorderedTranslations
    },

    preventLineBreak (itemKey, event) {
      if (FOOTER_CONSENT_FIELD_ORDER.includes(itemKey)) {
        event.preventDefault()
      }
    },

    getLangStatus (lang) {
      if (this.published.reviewed_languages && ~this.published.reviewed_languages.indexOf(lang)) {
        return 'published'
      } else if (~this.editor.reviewed_languages.indexOf(lang)) {
        return 'approved'
      } else {
        return 'reviewing'
      }
    },

    groupIcon (groupName) {
      if (groupName === 'product-infos') {
        return 'mdi-security'
      }
      if (groupName === 'footer') {
        return 'mdi-page-layout-footer'
      }
      if (groupName === 'cookie') {
        return 'mdi-cookie'
      }
      // Groups without their own icon (new config sections) show no icon.
      const icon = this.editor[groupName] && this.editor[groupName].icon
      if (!icon) {
        return ''
      }
      if (icon.startsWith('mdi-') || icon.startsWith('$')) {
        return icon
      }
      return `$${icon}`
    },

    groupTitle (groupName, groupIndex) {
      if (groupName === 'product-infos') {
        return this.$t('translations_auth_result')
      }
      if (groupName === 'track-and-trace') {
        return this.$t('translations_track_and_trace')
      }
      if (groupName === 'footer') {
        return this.$t('footer_consent_footer_section')
      }
      if (groupName === 'cookie') {
        return this.$t('footer_consent_cookie_section')
      }
      if (this.editor[groupName] && this.editor[groupName].type === 'custom-html') {
        return this.editor[groupName].label[this.defaultLanguage] || 'Custom HTML'
      }
      if (this.groupedTranslations[groupName][0].type === 'cms') {
        return this.groupedTranslations[groupName][0].value
      }
      return this.$t('translations_tab_number', { number: groupIndex + 1 })
    },

    getI18nKey (key, typeNumber) {
      const footerConsentLabels = {
        'footer:body': 'footer_body_label',
        'footer:background:image': 'footer_background_label',
        'footer:privacy:url': 'footer_privacy_url_label',
        'footer:gdpr:privacy': 'footer_privacy_button_label',
        'footer:gdpr:cookie': 'footer_cookie_button_label',
        'cookie:message': 'cookie_message_label',
        'cookie:accept': 'cookie_accept_label',
        'cookie:decline': 'cookie_decline_label',
        'cookie:policy': 'cookie_policy_label',
        'cookie:link:url': 'cookie_link_label'
      }
      if (footerConsentLabels[key]) {
        return this.$t(footerConsentLabels[key])
      }

      // Example of key: 'cms_01:body:2:data:content:large_text'
      const splitKeys = key.split(':')
      const fieldType = splitKeys[splitKeys.length - 1]

      // Custom HTML block keys: 'html_abc:_htmlBlocks:h1_0:custom-html'
      if (fieldType === 'custom-html' && splitKeys.length >= 3 && splitKeys[1] === '_htmlBlocks') {
        const blockId = splitKeys[2]
        return this.formatBlockLabel(blockId)
      }

      const subGroup = splitKeys.slice(0, 3).join(':')
      const fieldElement = splitKeys[splitKeys.length - 2]

      const keysWithMatchingSubgroup = Object.keys(this.translationsStringsWithoutHTMLTags).filter((key) => {
        const subgroup = key.split(':').slice(0, 3).join(':')
        return subgroup === subGroup
      })

      const formattedString = (keysWithMatchingSubgroup.length > 1)
        ? (typeNumber
          ? `${this.$t(fieldType)} ${typeNumber} — ${this.$t(fieldType + '_' + fieldElement)}`
          : `${this.$t(fieldType)} — ${this.$t(fieldType + '_' + fieldElement)}`)
        : (typeNumber
          ? `${this.$t(fieldType)} ${typeNumber}`
          : `${this.$t(fieldType)}`)

      return formattedString
    },

    formatBlockLabel (blockId) {
      // Parse block IDs like 'h1_0', 'p_1', 'a_2_text', 'a_2_href', 'img_3_src'
      const labelMap = {
        h1: this.$t('custom_html_block_heading'),
        h2: this.$t('custom_html_block_heading'),
        h3: this.$t('custom_html_block_heading'),
        h4: this.$t('custom_html_block_heading'),
        h5: this.$t('custom_html_block_heading'),
        h6: this.$t('custom_html_block_heading'),
        p: this.$t('custom_html_block_paragraph'),
        li: this.$t('custom_html_block_paragraph'),
        blockquote: this.$t('custom_html_block_paragraph'),
        button: this.$t('custom_html_block_button'),
        td: this.$t('custom_html_block_text'),
        th: this.$t('custom_html_block_text')
      }

      // Link blocks: a_2_text, a_2_href
      if (blockId.startsWith('a_') && blockId.endsWith('_text')) {
        return this.$t('custom_html_block_link_text')
      }
      if (blockId.startsWith('a_') && blockId.endsWith('_href')) {
        return this.$t('custom_html_block_link_url')
      }
      // Image blocks: img_3_src
      if (blockId.startsWith('img_') && blockId.endsWith('_src')) {
        return this.$t('custom_html_block_image_src')
      }

      // Standard blocks: tag_index
      const tag = blockId.replace(/_\d+$/, '')
      return labelMap[tag] || this.$t('custom_html_block_text')
    },

    addEnabledLanguages () {
      this.selectedTranslationLanguages.push(this.defaultLanguage)
      const addedLanguages = this.selectedTranslationLanguages.filter((lang) => !this.editor.enabled_languages.includes(lang))
      this.setEnabledLanguages(this.selectedTranslationLanguages)
      this.transferLinksFromDefaultLanguage(addedLanguages)
      this.selectedTranslationLanguages = []
    },
    transferLinksFromDefaultLanguage (addedLanguages) {
      addedLanguages.forEach(addedLang => {
        const footerConsentUrlFields = [this.editor.footer?.background, this.editor.footer?.privacy, this.editor.cookie?.link]
        footerConsentUrlFields.forEach(field => {
          if (field && typeof field === 'object' && field[this.defaultLanguage]) {
            field[addedLang] = field[this.defaultLanguage]
          }
        })

        Object.keys(this.editor).forEach(key => {
          if (typeof this.editor[key] === 'object' && this.editor[key].type === 'cms') {
            this.editor[key].body.forEach(field => {
              if (field.type === 'image' && field.data?.src) {
                field.data.src[addedLang] = field.data.src[this.defaultLanguage]
              }
              if ((field.type === 'button' || field.type === 'typeform' || field.type === 'social') && field.data?.url) {
                field.data.url[addedLang] = field.data.url[this.defaultLanguage]
              }
              if (field.type === 'video' && field.data?.content) {
                field.data.content[addedLang] = field.data.content[this.defaultLanguage]
              }
              if (field.type === 'iframe' && field.data?.url) {
                field.data.url[addedLang] = field.data.url[this.defaultLanguage]
                field.data.height[addedLang] = field.data.height[this.defaultLanguage]
              }
            })
          }
        })
      })
    },
    removeSelectedTranslationLanguage (lang) {
      this.selectedTranslationLanguages = this.selectedTranslationLanguages.filter((l) => l !== lang.key)
    },
    toggleLanguagesEditMode () {
      this.languagesEditDialog = true
    },
    onTranslationLanguagesChanged (enabledLanguages) {
      const addedLanguages = enabledLanguages.filter((lang) => !this.editor.enabled_languages.includes(lang))

      this.setEnabledLanguages(enabledLanguages)

      if (addedLanguages.length > 0) {
        this.transferLinksFromDefaultLanguage(addedLanguages)
      }

      const languagesToRemove = this.editor.reviewed_languages?.filter(lang => !enabledLanguages.includes(lang)) || []
      if (languagesToRemove.length > 0 && this.published.reviewed_languages) {
        languagesToRemove.forEach(lang => this.unpublishLanguage(lang))
      }
    },

    bulkApproveAndPublish (dialog) {
      if (this.languagesToPublish.indexOf('all') > -1) {
        this.enabledLanguages.forEach((lang) => {
          this.approveLanguage(lang)
        })
        this.publish()
      } else {
        this.languagesToPublish.forEach((lang) => {
          this.approveLanguage(lang)
        })
        if (this.editor.reviewed_languages.indexOf(this.editor.default_language) === -1) {
          this.approveLanguage(this.editor.default_language)
        }
        this.publish(this.languagesToPublish)
      }

      if (dialog) {
        this.bulkApproveDialog = false
        this.languagesToPublish = ['all']
      }
    },

    async publish (languages) {
      this.isPublishing = true
      await this.$store.dispatch('remotePublish', languages)
      if (languages) {
        amplitude.getInstance().logEvent('Publish approved language')
      }
      this.isPublishing = false
    },

    handleRichTextFocus (itemKey) {
      const textEditComponent = this.$refs['editor-' + itemKey]
      const componentInstance = Array.isArray(textEditComponent) ? textEditComponent[0] : textEditComponent
      if (componentInstance) {
        const richTextInputComponent = componentInstance.$children[0]
        richTextInputComponent.focusEnd()
      }
    },

    handleTextAreaInput (itemKey, itemType) {
      if (itemType !== 'video') return
      const videoUrl = this.translatedStrings[itemKey]
      this.debounceUpdateVideoUrl(itemKey, videoUrl)
    },

    convertToEmbedUrl (url) {
      if (url.indexOf('/watch?v=') > -1) {
        const videoId = url.split('/watch?v=')[1]
        return 'https://www.youtube.com/embed/' + videoId
      }
      return url
    },

    sanitizeVal (val) {
      return DOMPurify.sanitize(val)
    },

    sanitizeHTML (content) {
      return DOMPurify.sanitize(content)
    },

    isEditingImageLink (itemKey) {
      return this.tempImageLinks[itemKey] !== undefined
    },
    saveImageLink (itemKey) {
      this.translatedStrings[itemKey] = this.tempImageLinks[itemKey]
      this.$delete(this.tempImageLinks, itemKey)
    },
    cancelEditingImageLink (itemKey) {
      this.$delete(this.tempImageLinks, itemKey)
    },
    isDisplayableImage (string) {
      if (string && string.indexOf('{') > -1) {
        return false
      }
      return true
    },
    toggleImageEditMode (itemKey, itemType) {
      if (itemType === 'gallery') {
        return
      }
      if (!this.tempImageLinks[itemKey]) {
        this.$set(this.tempImageLinks, itemKey, '')
      } else if (this.isEditingImageLink(itemKey)) {
        this.cancelEditingImageLink(itemKey)
      } else {
        amplitude.getInstance().logEvent('Edit Widget', { itemType })
      }
    },
    onImageUpload (itemKey, url) {
      this.translatedStrings[itemKey] = url
    },

    isFileUrlItem (item) {
      return item.type === 'file' && item.element === 'url'
    },
    handleSTGallerySelected (itemKey, url) {
      this.translatedStrings[itemKey] = url
    },
    handleSTGalleryBtnClick (itemKey) {
      this.activeGalleryKey = itemKey
    },
    closeSTGalleryDialog () {
      this.activeGalleryKey = null
    },

    expandPreview () {
      this.changePreviewMode(true)
    },

    toggleGroup (groupName) {
      // this.$set(this.collapsedGroups, groupName, !this.collapsedGroups[groupName])
    },

    async translateAllLanguages (fromDialog) {
      if (fromDialog) {
        amplitude.getInstance().logEvent('Use machine translation 2')
      } else {
        amplitude.getInstance().logEvent('Use machine translation 1')
      }
      this.translating = true

      const { translationsStrings, translatedStrings } = translations.getTranslationsForLanguage(this.editor, this.defaultLanguage, null, true)
      this.translationsStrings = this.sortTranslations(translationsStrings)
      this.translatedStrings = this.sortTranslations(translatedStrings)

      this.$store.dispatch('machineTranslateLanguages', { source: this.defaultLanguage, strings: this.translationsStrings, destinations: this.editor.enabled_languages }).then((res) => {
        this.$store.dispatch('updateConfigStrings', { languages: this.enabledLanguagesSorted, strings: res, hasUsedMachineTranslate: true })
        this.translating = false
        if (fromDialog) {
          this.machineTranslateDialog = false
        }
        this.selectLanguage(this.defaultLanguage)
      })
    },

    async machineTranslate () {
      this.translating = true

      const { translationsStrings, translatedStrings } = translations.getTranslationsForLanguage(this.editor, this.defaultLanguage, this.selectedLanguage, true)
      this.translationsStrings = this.sortTranslations(translationsStrings)
      this.translatedStrings = this.sortTranslations(translatedStrings)

      this.$store.dispatch('machineTranslateLanguages', { source: this.defaultLanguage, strings: this.translationsStrings, destinations: [this.selectedLanguage] }).then((res) => {
        for (const [key, langs] of Object.entries(res)) {
          const value = (langs && langs[this.selectedLanguage]) || ''
          const splitKeys = key.split(':')
          const fieldElement = splitKeys[splitKeys.length - 2]
          const fieldType = splitKeys[splitKeys.length - 1]

          // Prevent links from being overwritten by the default language's unless empty, in which case copies over the default language's link
          if (fieldElement === 'url' || fieldType === 'url' || fieldType === 'image' || fieldType === 'video' || fieldType === 'iframe') {
            if (this.translatedStrings[key]) continue

            this.$set(this.translatedStrings, key, this.translationsStrings[key])
            continue
          }

          const textEditComponent = this.$refs['editor-' + key]
          const componentInstance = Array.isArray(textEditComponent) ? textEditComponent[0] : textEditComponent
          if (componentInstance) {
            this.$set(componentInstance.editorContentJson, 'content', value)
            const richTextInputComponent = componentInstance.$children[0]
            richTextInputComponent.updateContent(value)
          }
          this.$set(this.translatedStrings, key, value)
        }
      }).catch((err) => {
        console.log('error translating', err)
      }).finally(() => {
        this.translating = false
      })
    },

    exportAllTranslations () {
      amplitude.getInstance().logEvent('Download all translations')
      this.translationsMap = {}
      let csvContent = 'data:text/csv;charset=utf-8,'
      csvContent += `${this.$t('languages_' + this.defaultLanguage)}\t`

      this.enabledLanguagesSorted.forEach((lang, index) => {
        if (lang.key === this.defaultLanguage) return
        csvContent += `${this.$t('languages_' + lang.key)}`
        if (index < this.enabledLanguagesSorted.length - 1) {
          csvContent += '\t'
        } else {
          csvContent += '\n'
        }
      })

      this.getTranslationMap(this.editor, '')
      const sortedTranslationsMap = this.sortTranslations(this.translationsMap)

      for (const [key] of Object.entries(sortedTranslationsMap)) {
        if (sortedTranslationsMap[key][this.defaultLanguage]) {
          let string = sortedTranslationsMap[key][this.defaultLanguage]
          string = string.replace(/(?:\r\n|\r|\n)/g, '<br>')
          csvContent += `${string}\t`
        } else {
          csvContent += '\t'
        }
        this.enabledLanguagesSorted.forEach((lang, index) => {
          if (lang.key === this.defaultLanguage) return

          if (sortedTranslationsMap[key][lang.key]) {
            let string = sortedTranslationsMap[key][lang.key]
            string = string.replace(/(?:\r\n|\r|\n)/g, '<br>')
            csvContent += `${string}`
          }

          if (index < this.enabledLanguagesSorted.length - 1) {
            csvContent += '\t'
          }
        })
        csvContent += '\n'
      }

      const data = encodeURI(csvContent)
      const link = document.createElement('a')
      link.setAttribute('href', data)
      link.setAttribute('download', 'translations.csv')
      link.click()
    },

    exportTranslations () {
      amplitude.getInstance().logEvent('Download single translation', { language: this.selectedLanguage })
      let csvContent = 'data:text/csv;charset=utf-8,'
      csvContent += `${this.$t('languages_' + this.defaultLanguage)}\t${this.$t('languages_' + this.selectedLanguage)}\n`
      for (const [key, value] of Object.entries(this.translationsStrings)) {
        csvContent += `${value.replace(/(?:\r\n|\r|\n)/g, '<br>')}\t${this.translatedStrings[key] ? this.translatedStrings[key].replace(/(?:\r\n|\r|\n)/g, '<br>') : ''}\n`
      }

      const data = encodeURI(csvContent)
      const link = document.createElement('a')
      link.setAttribute('href', data)
      link.setAttribute('download', 'translations.csv')
      link.click()
    },

    getTranslationMap (config, currentKey) {
      for (const [key, value] of Object.entries(config)) {
        if (typeof value === 'object' && !Array.isArray(value)) {
          if (value && value[this.defaultLanguage] && typeof value[this.defaultLanguage] === 'string') {
            this.translationsMap[`${currentKey}:${key}`] = value
          } else if (value) {
            this.getTranslationMap(value, currentKey ? currentKey + ':' + key : key)
          }
        } else if (Array.isArray(value)) {
          value.forEach((item, index) => {
            if (typeof item === 'object') {
              this.getTranslationMap(item, currentKey ? currentKey + ':' + key + ':' + index : key + ':' + index)
            }
          })
        }
      }
    },

    onPasteTranslations (event) {
      const paste = (event.clipboardData || window.clipboardData).getData('text')

      // Allow normal paste behavior if content is single line (e.g. a url)
      if (!paste.includes('\n')) {
        return
      }

      event.preventDefault()

      const rows = paste.split('\n')
      let index = 0
      let rowIndex = 0
      let inputIndex = 0

      // Dataset is retrieved from v-textareas, while currentFieldIndex is hack to retrieve indexes from rich text fields
      inputIndex = parseInt(event.target.dataset.inputIndex) || this.currentFieldIndex || 0

      for (const [key] of Object.entries(this.translationsStrings)) {
        if (index >= inputIndex && rows[rowIndex]) {
          let string = rows[rowIndex]
          string = string.replace(/<br>/g, '\n').trimEnd()

          const textEditComponent = this.$refs['editor-' + key]
          const componentInstance = Array.isArray(textEditComponent) ? textEditComponent[0] : textEditComponent
          if (componentInstance) {
            this.$set(componentInstance.editorContentJson, 'content', string)
            const richTextInputComponent = componentInstance.$children[0]
            richTextInputComponent.updateContent(string)
          }
          const isVideoUrl = key.split(':').pop() === 'video'
          this.$set(this.translatedStrings, key, isVideoUrl ? this.convertToEmbedUrl(string) : string)
          rowIndex++
        }
        index++
      }
      console.log('AFTER PASTE', this.translatedStrings)
    },

    initPasteHandler () {
      const translationDiv = document.querySelector('#translations')
      if (translationDiv) {
        translationDiv.addEventListener('paste', (event) => {
          this.onPasteTranslations(event)
        })
      }
    },

    customClasses (itemType, isDefaultLangText) {
      const richTextComponents = ['large_text', 'text', 'consume', 'track-and-trace', 'custom-html', 'body']
      if (richTextComponents.includes(itemType)) {
        return {
          'custom-rich-input': true,
          'bg-light-gray': isDefaultLangText
        }
      }
    }
  },
  watch: {
    languagesToPublish (newVal, oldVal) {
      this.enabledLanguagesSearchInput = ''
      const allIndex = newVal.indexOf('all')
      if (!newVal.length || (~allIndex && newVal.length > 1 && !~oldVal.indexOf('all'))) {
        this.languagesToPublish = ['all']
      } else if (~allIndex && newVal.length > 1) {
        this.languagesToPublish.splice(allIndex, 1)
      }
    },
    selectedLanguage (newVal) {
      if (this.initialized && newVal && !this.eventHandlerInitialized) {
        this.$nextTick(() => {
          this.eventHandlerInitialized = true
          this.initPasteHandler()
        })
      }
    },
    translatedStrings: {
      handler (newVal) {
        Object.keys(newVal).forEach(key => {
          const editorComponent = this.$refs['editor-' + key]
          const componentInstance = Array.isArray(editorComponent) ? editorComponent[0] : editorComponent
          const content = componentInstance?.editorContentJson?.content
          if (!content) return

          const type = key.split(':').pop()
          const hasSingleLine = ['button', 'image', 'social', 'typeform', 'video', 'track-and-trace', 'custom-html'].includes(type)
          const processedContent = typeof content !== 'string'
            ? convertToSTCString(content, hasSingleLine)
            : content
          const sanitized = this.sanitizeHTML(processedContent)

          if (newVal[key] !== sanitized) {
            this.$set(this.translatedStrings, key, sanitized)
          }
        })
        this.debounceStringUpdate(newVal)
      },
      deep: true
    }
  },
  created () {
    this.debounceStringUpdate = utils.debounce((newVal) => {
      if (this.selectedLanguage) {
        this.$store.dispatch('updateConfigStrings', { languages: [this.selectedLanguage], strings: newVal })
      }
    }, 500)

    this.debounceUpdateVideoUrl = utils.debounce((itemKey, url) => {
      if (this.selectedLanguage) {
        const embeddedUrl = this.convertToEmbedUrl(url)
        this.$set(this.translatedStrings, itemKey, embeddedUrl)
      }
    }, 800)

    if (this.initialized) {
      if (!this.languageInitialized) {
        this.selectLanguage(this.defaultLanguage)
        this.languageInitialized = true
      }
    }
  },
  activated () {
    // Prevent calling selectLanguage in both created and activated
    if (!this.languageInitialized) {
      this.selectLanguage(this.defaultLanguage)
    }
    this.languageInitialized = false
    if (!this.isMenuCollapsed) {
      this.toggleAppSidebar()
    }
    // Opened via the "change reference language" link (e.g. from the template picker)
    if (this.$route.query.editLanguage) {
      this.languagesEditDialog = true
      const query = { ...this.$route.query }
      delete query.editLanguage
      this.$router.replace({ query })
    }
  },
  deactivated () {
    if (this.isMenuCollapsed) {
      this.toggleAppSidebar()
    }
  }
}
</script>

<style lang="sass" scoped>

@import '@/variables.sass'
.global
  height: 100%
  position: relative
  border-bottom: none
  margin: 0px !important
  .phone-preview-btn
    position: absolute
    top:8px
    right: 10px
    @include lg
      display: none

.scroll-ctn
  height: 100vh
  overflow: auto
  margin-bottom: 50px
  &.has-language
    height: calc(100vh - 55px)
    .phone-preview-btn
      top: 8px
      right: 10px

#translations
  height: 100%
  flex-wrap: nowrap
  min-width: 0

.translation-sidebar
  flex: 0 0 304px
  max-width: 304px
  border-right: 1px solid #DCDCDC

.translation-content
  min-width: 0

.translations-languages-selection
  width: 600px
  max-width: 600px
  margin-left: auto
  margin-right: auto

.selected
  background: rgba(var(--primary), 0.2) !important

.empty-state
  .title
    color: #0070B6
    margin-bottom: 10px

.published-lang-ctn
  background: rgba(var(--primary), 0.2) !important
  font-style: italic

.lang-pill
  flex-shrink: 0
  width: 16px
  height: 16px
  margin-right: 4px
  border-radius: 50%
  background-color: #D9D9D9
  &.approved
    background-color: #F5A623
  &.published
    background-color: #3FC135

.language
  cursor: pointer
  background: white
  &:nth-child(odd)
    background: #F3F3F3

.publish-btn
  width: 280px
  letter-spacing: 0.8px

.v-btn.btn-text-white
  color: white !important

.csv-hint
  color: black
  font-size: 16px
  max-width: 350px
  text-align: center
  ul
    text-align: left
  .hint-title
    text-align: left
  .warning
    padding: 5px
    text-align: left
    border-radius: 5px

.hint
  font-style: italic

.translation-table-header
  background: rgba(var(--primary))
  color: white

.translation-table
  width: 100%
  overflow-x: auto

.translation-table-subheader
  padding: 3px 12px

.translation-table-subheader, .group-header, .medium-gray-bg
  background: #F1F1F1

.translation-groups
  &:last-child
    border-bottom: 1px solid #777

.translation-group
  border-left: 1px solid #777
  border-right: 1px solid #777
  border-top: 1px solid #777

.group-header
  border-bottom: 1px solid #D9D9D9

.group-row
  border-bottom: 1px solid #D9D9D9
  margin: 1px 0
  &:last-child
    margin-bottom: 0 !important

.translation-key
  width: 200px

.translation-value
  height: 100%
  word-break: break-word
  white-space: pre-wrap
  &.border
    border: 1px solid #666

.v-textarea
  height: 100%
  ::v-deep .v-input__control
    height: 100%
    .v-input__slot
      align-items: flex-start !important
      height: 100%
      border-radius: 0px
      textarea
        caret-color: black !important
    .v-input__slot:hover
      outline: 2px rgba(var(--primary)) solid
    .v-input__slot::before, .v-input__slot::after
      border-style: none !important
    .v-text-field__slot
      textarea
        margin-left: 10px
        margin-right: 10px
        margin-top: 10px

.rich-text-container
  height: 100%
  display: flex

.image-header
    max-height: 60px

.edit-image-toolbar
  width: 100%
  border-bottom: 1px gray solid

.edit-image-toolbar-actions
  width: fit-content
  margin-left: auto

.outline-blue:hover, .custom-textarea:focus-within
  outline: 2px rgba(var(--primary)) solid

.label
  text-transform: uppercase
  color: $label-grey

.hide-lg
  @include lg
    display: none

.headline
  background: #F1F1F1

.light-gray-bg
  background: #F9F9F9

.max-w-40
  max-width: 40%

.min-w-60
  min-width: 60%

.w-49
  width: 49%

.cursor-default
  cursor: default
</style>
